import { useEffect } from "react"

import { Checkbox, Form, Input, Select } from "@/atoms"
import {
  getAllowedAuthMethods,
  getEndpointProblem,
  getIncompatiblePair,
  hasUsername,
  isPasswordMethod,
  isValidScope,
  normalizeScope,
  PACKAGE_MANAGERS
} from "@/pages/IntegrationsPage/helpers/packageRegistry"

import styles from "./FormPackageRegistry.module.scss"

import type { RegistryPackageManager } from "@/api/useIntegrations.types"
import type { PackageRegistryFormValues } from "@/pages/IntegrationsPage/hooks/usePackageRegistryAPI.types"
import type { FormPackageRegistryProps } from "."

// The saved secret is never sent back; the dots show one is stored. Typing replaces it.
// JFrog Artifactory's realm, the most common; Nexus uses "Sonatype Nexus Repository Manager".
const DEFAULT_SBT_REALM = "Artifactory Realm"

const SAVED_SECRET = "••••••••••••••••"

const PREFIX = "integrations.packageRegistry.form"

export const FormPackageRegistry = (props: FormPackageRegistryProps) => {
  const { form, formId, entry, onFieldsChange, onFinish, t } = props
  const isNew = !entry
  // undefined until the form has mounted and the watch has read it
  const watchedPackageManagers: RegistryPackageManager[] | undefined =
    Form.useWatch("packageManagers", form)
  const packageManagers = watchedPackageManagers || []
  const authMethod = Form.useWatch("authMethod", form)
  const allowedMethods = getAllowedAuthMethods(packageManagers)
  const allowedKey = allowedMethods.join(",")
  const npmTicked = packageManagers.includes("npm")
  const sbtTicked = packageManagers.includes("sbt")
  const required = { required: true, message: t("error.requiredField") }

  // Keep the auth method one the ticked package managers accept: re-filter on change, and
  // pick the only option when there is just one.
  // Before the watch has read the form it reports no package managers, which would clear
  // the saved auth method of an entry being edited, so wait for it.
  const watchReady = watchedPackageManagers !== undefined

  useEffect(() => {
    if (!watchReady) return

    const current = form.getFieldValue("authMethod")

    if (allowedMethods.length === 1 && current !== allowedMethods[0])
      form.setFieldValue("authMethod", allowedMethods[0])
    else if (current && !allowedMethods.includes(current))
      form.setFieldValue("authMethod", undefined)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowedKey, watchReady])

  const initialValues: Partial<PackageRegistryFormValues> = entry
    ? {
        endpoint: entry.endpoint,
        packageManagers: entry.packageManagers,
        scopes: entry.scopes,
        authMethod: entry.authMethod || undefined,
        username: entry.username || undefined,
        realm: entry.realm || undefined
      }
    : { packageManagers: [], scopes: [], realm: DEFAULT_SBT_REALM }

  const secretKey = isPasswordMethod(authMethod) ? "password" : "token"

  return (
    <Form
      className={styles.form}
      form={form}
      id={formId}
      layout="vertical"
      initialValues={initialValues}
      onFieldsChange={onFieldsChange}
      onFinish={onFinish}
    >
      <Form.Item
        validateFirst
        label={t(`${PREFIX}.endpoint.label`)}
        name="endpoint"
        required={false}
        rules={
          isNew
            ? [
                required,
                {
                  validator: (_, value) => {
                    const problem = getEndpointProblem(value)

                    if (!problem) return Promise.resolve()

                    return Promise.reject(
                      new Error(
                        t(`${PREFIX}.endpoint.errors.${problem.problem}`, {
                          host: problem.host
                        })
                      )
                    )
                  }
                }
              ]
            : []
        }
      >
        <Input
          ref={props.focusRef}
          disabled={!isNew}
          placeholder={t(`${PREFIX}.endpoint.placeholder`)}
        />
      </Form.Item>

      <Form.Item
        className={styles.choices}
        label={t(`${PREFIX}.packageManagers.label`)}
        name="packageManagers"
        required={false}
        rules={[
          {
            required: true,
            type: "array",
            min: 1,
            message: t("error.requiredField")
          },
          {
            validator: (_, value: RegistryPackageManager[]) => {
              const pair = getIncompatiblePair(value)

              if (!pair) return Promise.resolve()

              const [first, second] = pair.map((pm) =>
                t(`${PREFIX}.packageManagers.options.${pm}`)
              )

              return Promise.reject(
                new Error(
                  t(`${PREFIX}.packageManagers.incompatible`, { first, second })
                )
              )
            }
          }
        ]}
      >
        <Checkbox.Group
          options={PACKAGE_MANAGERS.map((pm) => ({
            value: pm,
            label: t(`${PREFIX}.packageManagers.options.${pm}`)
          }))}
        />
      </Form.Item>

      {npmTicked && (
        <Form.Item
          label={t(`${PREFIX}.scopes.label`)}
          name="scopes"
          normalize={(value: string[]) =>
            (value || []).map(normalizeScope).filter(Boolean)
          }
          rules={[
            {
              validator: (_, value: string[] = []) => {
                const invalid = value.find((scope) => !isValidScope(scope))

                if (!invalid) return Promise.resolve()

                return Promise.reject(
                  new Error(t(`${PREFIX}.scopes.invalid`, { scope: invalid }))
                )
              }
            }
          ]}
        >
          <Select
            mode="tags"
            open={false}
            suffixIcon={null}
            tokenSeparators={[",", " "]}
            placeholder={t(`${PREFIX}.scopes.placeholder`)}
          />
        </Form.Item>
      )}

      {sbtTicked && allowedMethods.length > 0 && (
        <Form.Item
          label={t(`${PREFIX}.realm.label`)}
          name="realm"
          required={false}
          rules={[
            required,
            { whitespace: true, message: t("error.requiredField") }
          ]}
        >
          <Input
            autoComplete="off"
            placeholder={t(`${PREFIX}.realm.placeholder`)}
          />
        </Form.Item>
      )}

      {/* Every package manager takes one auth method: the form picks it (see the effect above),
          and the credential fields below follow from it. */}
      <Form.Item name="authMethod" hidden rules={[required]}>
        <Input />
      </Form.Item>

      {hasUsername(authMethod) && (
        <Form.Item
          label={t(`${PREFIX}.username.label`)}
          name="username"
          required={false}
          rules={[
            required,
            { whitespace: true, message: t("error.requiredField") }
          ]}
        >
          <Input
            autoComplete="off"
            placeholder={t(`${PREFIX}.username.placeholder`)}
          />
        </Form.Item>
      )}

      {authMethod && (
        <Form.Item
          label={t(`${PREFIX}.${secretKey}.label`)}
          name="secret"
          required={false}
          rules={
            isNew
              ? [
                  required,
                  { whitespace: true, message: t("error.requiredField") }
                ]
              : []
          }
        >
          <Input
            type="password"
            autoComplete="new-password"
            placeholder={
              isNew ? t(`${PREFIX}.${secretKey}.placeholder`) : SAVED_SECRET
            }
          />
        </Form.Item>
      )}
    </Form>
  )
}
